import React from 'react';
import { X, AlertCircle } from 'lucide-react';
import { InlineLoadingState } from '../../../components/LoadingSpinner';
import { AppModal } from '../../../components/AppModal';
import { PdfDocumentViewer } from '../../../components/PdfDocumentViewer';
import { PdfPrintButton } from '../../../components/PdfPrintButton';
import { PdfMailShareButton } from '../../../components/PdfMailShareButton';
import type { TenderDocument } from '../types';

export function isTenderDocumentPdf(doc: TenderDocument): boolean {
  const m = (doc.mime_type || '').toLowerCase();
  if (m === 'application/pdf' || m.includes('pdf')) return true;
  return doc.filename.toLowerCase().endsWith('.pdf');
}

type Props = {
  ausschreibungId: string;
  document: TenderDocument | null;
  onClose: () => void;
};

export const TenderDocumentPdfModal: React.FC<Props> = ({ ausschreibungId, document, onClose }) => {
  const open = document != null;
  const [url, setUrl] = React.useState<string | null>(null);
  const [loadError, setLoadError] = React.useState<string | null>(null);
  const [loading, setLoading] = React.useState(false);

  React.useEffect(() => {
    if (!document || !open) {
      setUrl(null);
      setLoadError(null);
      setLoading(false);
      return;
    }
    if (!isTenderDocumentPdf(document)) {
      setUrl(null);
      setLoadError('Nur PDF-Dateien können in der Vorschau geöffnet werden.');
      setLoading(false);
      return;
    }
    let cancelled = false;
    setLoading(true);
    setLoadError(null);
    setUrl(null);
    fetch(
      `/api/ausschreibungen/${encodeURIComponent(ausschreibungId)}/documents/${encodeURIComponent(document.id)}/signed`,
      { credentials: 'include' },
    )
      .then(async (r) => {
        const raw = (await r.json().catch(() => ({}))) as { url?: string; error?: string };
        if (!r.ok) {
          throw new Error(typeof raw.error === 'string' ? raw.error : 'Vorschau nicht möglich.');
        }
        if (typeof raw.url !== 'string' || !raw.url) {
          throw new Error('Keine gültige URL erhalten.');
        }
        return raw.url;
      })
      .then((u) => {
        if (!cancelled) setUrl(u);
      })
      .catch((e) => {
        if (!cancelled) setLoadError(e instanceof Error ? e.message : String(e));
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [ausschreibungId, document?.id]);

  React.useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [open, onClose]);

  if (!document) return null;

  return (
    <AppModal
      open={open}
      onClose={onClose}
      overlayClassName="z-[220]"
      panelClassName="md:max-w-6xl md:w-full"
      ariaLabelledBy="tender-pdf-modal-title"
    >
      <div className="flex flex-col h-full min-h-0 md:max-h-[90vh]">
        <div className="flex items-center justify-between gap-3 px-4 py-3 border-b border-app-border shrink-0">
          <h2
            id="tender-pdf-modal-title"
            className="text-sm font-semibold text-app-text truncate pr-2"
            title={document.filename}
          >
            {document.filename}
          </h2>
          <div className="flex shrink-0 items-center gap-1">
            {url && !loading && !loadError ? (
              <>
                <PdfMailShareButton
                  url={url}
                  filename={document.filename}
                  variant="icon"
                  onBeforeOpen={onClose}
                  className="inline-flex h-9 w-9 items-center justify-center rounded-lg text-app-text-subtle hover:bg-app-hover hover:text-app-text transition"
                />
                <PdfPrintButton
                  url={url}
                  variant="icon"
                  className="inline-flex h-9 w-9 items-center justify-center rounded-lg text-app-text-subtle hover:bg-app-hover hover:text-app-text transition"
                />
              </>
            ) : null}
            <button
              type="button"
              onClick={onClose}
              className="shrink-0 p-2 rounded-lg text-app-text-subtle hover:bg-app-hover hover:text-app-text transition"
              aria-label="Schließen"
            >
              <X className="w-5 h-5" />
            </button>
          </div>
        </div>
        <div className="flex-1 min-h-0 flex flex-col bg-app-surface-muted">
          {loading && (
            <div className="flex-1 grid place-items-center p-8">
              <InlineLoadingState label="Wird geladen…" />
            </div>
          )}
          {!loading && loadError && (
            <div className="flex-1 flex items-start gap-2 p-4 m-4 rounded-lg border border-amber-200 bg-amber-50 text-sm text-amber-900">
              <AlertCircle className="w-5 h-5 shrink-0 mt-0.5" />
              <p>{loadError}</p>
            </div>
          )}
          {!loading && !loadError && url && (
            <PdfDocumentViewer
              url={url}
              title={document.filename}
              className="min-h-[70vh] w-full md:min-h-[75vh]"
            />
          )}
        </div>
      </div>
    </AppModal>
  );
};
